import React, { useCallback, useEffect, useState } from "react";
import {
  Phone, PhoneCall, ShieldCheck, Mic, ArrowRightLeft, ClipboardCheck, Play, Square,
  CheckCircle2, XCircle, Settings2, Send,
} from "lucide-react";
import { PageHeader, Button, Input, Textarea, Checkbox, StatusBadge, LoadingBlock, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { WebCallCard } from "./voice/WebCallCard";
import { BusinessPhoneCard } from "./voice/BusinessPhoneCard";

const OUTCOME_TONE: Record<string, "green" | "amber" | "red" | "slate" | "blue"> = {
  appointment_booked: "green", qualified: "green", lead_captured: "blue", faq_resolved: "blue",
  appointment_rescheduled: "blue", appointment_cancelled: "amber", transferred: "blue",
  message_taken: "amber", emergency_escalated: "red", caller_disconnected: "slate", spam: "slate", failed: "red",
};

export default function Voice() {
  // Note: voice-disclosure/voice-save-disclosure render only when recording is ON;
  // voice-code-input/voice-confirm-btn/voice-sim-code render only after a code is sent.
  const [meta, setMeta] = useState<any>(null);
  const [settings, setSettings] = useState<any>(null);
  const [readiness, setReadiness] = useState<any>(null);
  const [verifyNum, setVerifyNum] = useState("+1");
  const [codeSent, setCodeSent] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const [session, setSession] = useState<any>(null);
  const [fromNum, setFromNum] = useState("(312) 555-7788");
  const [turnText, setTurnText] = useState("");
  const [transcript, setTranscript] = useState<{ speaker: string; text: string }[]>([]);
  const [outcome, setOutcome] = useState<any>(null);

  const refresh = useCallback(() => {
    api.voiceSettings().then(setSettings).catch((e) => toast.error(formatApiError(e)));
    api.voiceReadiness().then(setReadiness).catch(() => {});
  }, []);
  useEffect(() => { api.voiceMeta().then(setMeta).catch(() => {}); refresh(); }, [refresh]);

  const save = async (patch: any) => {
    try { setSettings(await api.voiceUpdateSettings(patch)); toast.success("Voice settings saved"); await api.voiceReadiness().then(setReadiness); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const doVerify = async () => {
    try { const r = await api.voiceVerifyNumber(verifyNum); setCodeSent(r.code); toast.success("Verification code generated (simulated)"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doConfirm = async () => {
    try { await api.voiceConfirmNumber(verifyNum, code); setCodeSent(null); setCode(""); toast.success("Number verified"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doTestCall = async () => {
    try { await api.voiceStartTestCall(verifyNum); toast.success("Test call requested"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doActivate = async () => {
    try { await api.voiceActivate(); toast.success("Public number activated"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doDeactivate = async () => { try { await api.voiceDeactivate(); toast.success("Public number deactivated"); refresh(); } catch (e) { toast.error(formatApiError(e)); } };

  const startSim = async () => {
    setBusy(true); setOutcome(null); setTranscript([]);
    try {
      const r = await api.voiceSimStart({ from_number: fromNum, caller_name: "Test Caller" });
      setSession(r);
      setTranscript([{ speaker: "ai", text: r.session_config.first_message }]);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const sendTurn = async () => {
    if (!turnText.trim() || !session) return;
    setBusy(true);
    const text = turnText.trim();
    setTranscript((t) => [...t, { speaker: "customer", text }]);
    setTurnText("");
    try {
      const r = await api.voiceSimTurn({ call_record_id: session.call_record_id, conversation_id: session.conversation_id, text });
      if (r.response) setTranscript((t) => [...t, { speaker: "ai", text: r.response }]);
      if (r.transfer) toast[r.transfer.transferred ? "success" : "error"](r.transfer.transferred ? `Transferred (${r.transfer.reason})` : `Transfer failed — message offered`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const endSim = async (reason?: string) => {
    if (!session) return;
    setBusy(true);
    try {
      const r = await api.voiceSimEnd({ call_record_id: session.call_record_id, disconnect_reason: reason });
      setOutcome(r); setSession(null); refresh();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!settings) return <div className="p-2"><LoadingBlock rows={6} /></div>;

  return (
    <div data-testid="page-voice" className="space-y-6">
      <PageHeader title="Voice Receptionist" testid="voice-header"
        description="Configure the AI voice line, run a controlled test call, and pass readiness checks before activating your public number. (Provider + Twilio connect later — nothing is faked.)" />

      <WebCallCard configured={!!meta?.provider_configured} />

      <BusinessPhoneCard />

      {!meta?.provider_configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="voice-provider-banner">
          The voice provider is not connected yet (VOICE_PROVIDER = <b>{meta?.provider ?? "not_configured"}</b>). You can configure everything and simulate calls now; live calls and the public number stay disabled until keys are added.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Settings */}
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="voice-settings">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Settings2 className="h-4 w-4 text-blue-500" /> Configuration</div>
          <label className="text-xs font-medium text-slate-500">Business phone number</label>
          <div className="mb-3 flex gap-2">
            <Input defaultValue={settings.business_number ?? ""} placeholder="+1 (312) 555-0000" data-testid="voice-business-number" id="voice-biz" />
            <Button size="sm" variant="outline" onClick={() => save({ business_number: (document.getElementById("voice-biz") as HTMLInputElement)?.value })} data-testid="voice-save-business">Save</Button>
          </div>
          <label className="text-xs font-medium text-slate-500">Human transfer number</label>
          <div className="mb-3 flex gap-2">
            <Input defaultValue={settings.transfer_number ?? ""} placeholder="+1 (312) 555-0111" data-testid="voice-transfer-number" id="voice-xfer" />
            <Button size="sm" variant="outline" onClick={() => save({ transfer_number: (document.getElementById("voice-xfer") as HTMLInputElement)?.value })} data-testid="voice-save-transfer">Save</Button>
          </div>

          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Mic className="h-4 w-4 text-slate-400" /><span className="text-sm font-medium text-slate-700">Record calls</span></div>
              <Checkbox checked={!!settings.recording_enabled} onChange={(v: boolean) => save({ recording_enabled: v, recording_disclosure: settings.recording_disclosure })} testid="voice-recording-toggle" />
            </div>
            <p className="mt-1 text-xs text-slate-400">Off by default. Recording laws vary — a spoken disclosure is required when on.</p>
            {settings.recording_enabled && (
              <div className="mt-2 flex gap-2">
                <Input defaultValue={settings.recording_disclosure ?? ""} placeholder="Spoken disclosure" data-testid="voice-disclosure" id="voice-disc" />
                <Button size="sm" variant="outline" onClick={() => save({ recording_disclosure: (document.getElementById("voice-disc") as HTMLInputElement)?.value })} data-testid="voice-save-disclosure">Save</Button>
              </div>
            )}
          </div>

          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700"><ArrowRightLeft className="h-4 w-4 text-slate-400" /> Transfer to a human when…</div>
            {[
              ["transfer_emergencies", "an emergency is detected"],
              ["transfer_complaints", "the caller has a complaint"],
              ["transfer_low_confidence", "the AI is not confident"],
              ["transfer_repeated_misunderstanding", "there is repeated misunderstanding"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 py-1 text-sm text-slate-600">
                <Checkbox checked={!!settings[key]} onChange={(v: boolean) => save({ [key]: v })} testid={`voice-${key}`} /> {label}
              </label>
            ))}
          </div>
        </div>

        {/* Readiness */}
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="voice-readiness">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><ClipboardCheck className="h-4 w-4 text-indigo-500" /> Readiness checks</div>
          {!readiness ? <LoadingBlock rows={4} /> : (
            <>
              <div className="space-y-1.5">
                {readiness.checks.map((c: any) => (
                  <div key={c.key} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2" data-testid={`readiness-${c.key}`}>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      {c.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-slate-300" />}
                      {c.label}
                    </div>
                    <span className="text-xs text-slate-400">{c.detail}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                <div className="flex items-center gap-2 text-sm">
                  <ShieldCheck className={`h-4 w-4 ${readiness.number_active ? "text-emerald-500" : "text-slate-300"}`} />
                  Public number: <StatusBadge tone={readiness.number_active ? "green" : "slate"}>{readiness.number_active ? "Active" : "Inactive"}</StatusBadge>
                </div>
                {readiness.number_active
                  ? <Button size="sm" variant="outline" onClick={doDeactivate} data-testid="voice-deactivate">Deactivate</Button>
                  : <Button size="sm" disabled={!readiness.ready} onClick={doActivate} data-testid="voice-activate">Activate number</Button>}
              </div>
              {!readiness.ready && <p className="mt-1 text-xs text-slate-400">All checks must pass before the public number can go live.</p>}
            </>
          )}
        </div>
      </div>

      {/* Phone test call (real PSTN) */}
      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="voice-test-call">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800"><PhoneCall className="h-4 w-4 text-emerald-500" /> Phone test call (real PSTN)</div>
        {!meta?.telephony_configured ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="voice-testcall-twilio-required">
            <p className="font-medium">Twilio connection required for real phone test calls.</p>
            <p className="mt-1">Connect Twilio (Trust Hub KYC / A2P 10DLC) to place a real call to a verified number. Until then you can test the actual AI voice agent two other ways:</p>
            <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
              <li><b>Browser / web test call</b> — use “Talk to your AI (web call)” above. No phone number needed.</li>
              <li><b>Simulated inbound call</b> — use the simulator below to type as a caller.</li>
            </ul>
            <button
              type="button"
              className="mt-2 inline-flex cursor-not-allowed items-center gap-1 rounded-md border border-amber-300 px-2.5 py-1 font-medium text-amber-700 opacity-70"
              disabled
              data-testid="voice-testcall-btn-disabled"
            >
              <Phone className="h-3.5 w-3.5" /> Phone test call (connect Twilio)
            </button>
          </div>
        ) : (
          <>
            <p className="mb-2 text-xs text-slate-400">Verify a number you own, then place a real call to it.</p>
            <div className="flex flex-wrap items-center gap-2">
              <Input value={verifyNum} onChange={(e) => setVerifyNum(e.target.value)} placeholder="+1 (312) 555-1234" className="w-56" data-testid="voice-verify-number" />
              {!codeSent
                ? <Button size="sm" variant="outline" onClick={doVerify} data-testid="voice-verify-btn">Send code</Button>
                : (<>
                    <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" className="w-28" data-testid="voice-code-input" />
                    <Button size="sm" variant="outline" onClick={doConfirm} data-testid="voice-confirm-btn">Confirm</Button>
                    <span className="text-xs text-slate-400">Code (simulated): <b data-testid="voice-sim-code">{codeSent}</b></span>
                  </>)}
              <Button size="sm" onClick={doTestCall} data-testid="voice-testcall-btn"><Phone className="h-4 w-4" /> Request test call</Button>
            </div>
            {settings.verified_test_numbers?.length > 0 && (
              <p className="mt-2 text-xs text-slate-500" data-testid="voice-verified-list">Verified: {settings.verified_test_numbers.join(", ")}</p>
            )}
          </>
        )}
      </div>

      {/* Simulator */}
      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="voice-simulator">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><PhoneCall className="h-4 w-4 text-blue-500" /> Simulate an inbound call</div>
        {!session ? (
          <div className="flex items-center gap-2">
            <Input value={fromNum} onChange={(e) => setFromNum(e.target.value)} placeholder="Caller number" className="w-56" data-testid="voice-sim-from" />
            <Button onClick={startSim} disabled={busy} data-testid="voice-sim-start"><Play className="h-4 w-4" /> Start call</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <StatusBadge tone="green">In call</StatusBadge>
            <Button size="sm" variant="outline" onClick={() => endSim("user_hangup")} disabled={busy} data-testid="voice-sim-end"><Square className="h-4 w-4" /> End call</Button>
          </div>
        )}

        {(transcript.length > 0) && (
          <div className="mt-3 max-h-72 space-y-2 overflow-y-auto rounded-md border border-slate-100 bg-slate-50 p-3" data-testid="voice-transcript">
            {transcript.map((s, i) => (
              <div key={i} className={`flex ${s.speaker === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-sm ${s.speaker === "customer" ? "bg-blue-600 text-white" : "bg-white text-slate-700 border border-slate-200"}`}>{s.text}</div>
              </div>
            ))}
          </div>
        )}

        {session && (
          <div className="mt-2 flex items-center gap-2">
            <Textarea value={turnText} onChange={(e) => setTurnText(e.target.value)} rows={2} placeholder="Speak as the caller… (try 'my AC is broken', 'I want a person', 'do you cover 60601?')" data-testid="voice-turn-input" />
            <Button onClick={sendTurn} disabled={busy || !turnText.trim()} data-testid="voice-turn-send"><Send className="h-4 w-4" /></Button>
          </div>
        )}

        {outcome && (
          <div className="mt-3 rounded-md border border-slate-100 bg-slate-50 p-3" data-testid="voice-outcome">
            <div className="flex items-center gap-2"><span className="text-xs text-slate-400">Outcome</span><StatusBadge tone={OUTCOME_TONE[outcome.outcome] ?? "slate"} data-testid="voice-outcome-badge">{outcome.outcome}</StatusBadge>{outcome.duration_seconds != null && <span className="text-xs text-slate-400">{outcome.duration_seconds}s</span>}</div>
            <p className="mt-1.5 text-sm text-slate-700" data-testid="voice-summary">{outcome.summary}</p>
          </div>
        )}
      </div>
    </div>
  );
}
